import React, { useMemo, useState } from 'react';
import { Box, InputBase } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TitleIcon from '@mui/icons-material/Title';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SlideObject } from '../types';
import { PRES } from '../chrome';
import { applyLayerOrder, layerDisplayName, layersFrontToBack } from './layerList';

interface LayersPanelProps {
  objects: SlideObject[];
  selectedObjectId: string | null;
  selectedObjectIds?: string[];
  onSelect: (id: string, options?: { isolate?: boolean }) => void;
  onReorder: (objects: SlideObject[]) => void;
  onRename: (id: string, name: string) => void;
}

const LayersPanel: React.FC<LayersPanelProps> = ({ objects, selectedObjectId, selectedObjectIds, onSelect, onReorder, onRename }) => {
  const ordered = useMemo(() => layersFrontToBack(objects), [objects]);
  const ids = useMemo(() => ordered.map((object) => object.id), [ordered]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  return (
    <Box sx={{ p: 1, minHeight: 0, overflow: 'auto' }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const overId = event.over?.id;
          if (!overId || event.active.id === overId) return;
          const from = ids.indexOf(String(event.active.id));
          const to = ids.indexOf(String(overId));
          if (from < 0 || to < 0) return;
          onReorder(applyLayerOrder(objects, arrayMove(ids, from, to)));
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {ordered.map((object) => (
            <LayerRow
              key={object.id}
              object={object}
              selected={object.id === selectedObjectId || Boolean(selectedObjectIds?.includes(object.id))}
              grouped={Boolean(object.groupId)}
              onSelect={() => onSelect(object.id, object.groupId ? { isolate: true } : undefined)}
              onRename={(name) => onRename(object.id, name)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

const LayerRow: React.FC<{
  object: SlideObject;
  selected: boolean;
  grouped?: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
}> = ({ object, selected, grouped, onSelect, onRename }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: object.id });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(object.name || '');
  const locked = object.locks.position || object.locks.size || object.locks.content;

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.5,
        py: 0.4,
        mb: 0.25,
        borderRadius: 0.75,
        bgcolor: selected ? PRES.cream : isDragging ? PRES.canvas : 'transparent',
        cursor: 'pointer',
        opacity: isDragging ? 0.7 : 1,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        onClick={(event) => event.stopPropagation()}
        sx={{ display: 'flex', color: PRES.muted, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
      <LayerTypeIcon object={object} />
      {editing ? (
        <InputBase
          autoFocus
          value={draft}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            onRename(draft.trim());
            setEditing(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
            if (event.key === 'Escape') setEditing(false);
          }}
          sx={{ flex: 1, fontSize: 12, color: PRES.textPrimary, bgcolor: PRES.white, px: 0.5, borderRadius: 0.5 }}
        />
      ) : (
        <Box
          onDoubleClick={(event) => {
            event.stopPropagation();
            setDraft(object.name || layerDisplayName(object));
            setEditing(true);
          }}
          sx={{
            flex: 1, fontSize: 12, color: PRES.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {layerDisplayName(object)}
        </Box>
      )}
      {grouped && <GroupWorkOutlinedIcon sx={{ fontSize: 14, color: PRES.muted }} titleAccess="Grupp" />}
      {locked && <LockOutlinedIcon sx={{ fontSize: 14, color: PRES.muted }} />}
    </Box>
  );
};

const LayerTypeIcon: React.FC<{ object: SlideObject }> = ({ object }) => {
  const sx = { fontSize: 16, color: PRES.textSecondary, flexShrink: 0 };
  if (object.type === 'text') return <TitleIcon sx={sx} />;
  if (object.type === 'image') return <ImageOutlinedIcon sx={sx} />;
  if (object.type === 'product') return <Inventory2OutlinedIcon sx={sx} />;
  if (object.style?.shape === 'line') return <HorizontalRuleIcon sx={sx} />;
  return <CategoryOutlinedIcon sx={sx} />;
};

export default LayersPanel;
